import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

import BaseButton from "@/components_v2/Button/BaseButton";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import CheckboxListItem from "@/components_v2/List/CheckboxListItem";
import WarningCircle from "@/assets/icons/warning-circle.svg?react";
import WarningTriangle from "@/assets/icons/warning.svg?react";
import { QuestionSuggestionsProps } from "../types";
import { getEvidenceTitle } from "@/domains/evidence/hooks/useEvidenceFields";
import { useGetQuestionSuggestionsQuery } from "@/redux/service";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import { OptionType, SuggestionType } from "@/shared/utils/constants";
import {
  ChoiceSuggestion,
  ChoiceTextSuggestion,
  PromptSuggestion,
  TextNumberSuggestion,
} from "@/redux/survey/types";

const QuestionSuggestions = ({
  questionId,
  questionType,
  onDismiss,
  onApply,
  disabledSuggestions,
}: QuestionSuggestionsProps) => {
  const { t } = useTranslation();

  const { data: suggestionsData, isLoading: isLoadingSuggestions } =
    useGetQuestionSuggestionsQuery(questionId);

  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [selectedChoiceTextValues, setSelectedChoiceTextValues] = useState<
    Set<string>
  >(new Set());

  const isChoiceQuestion = [
    OptionType.SINGLE_CHOICE.value,
    OptionType.MULTIPLE_CHOICE.value,
  ].includes(questionType);
  const isPromptQuestion = questionType === OptionType.PROMPT.value;
  const isTextNumberQuestion = [
    OptionType.TEXT.value,
    OptionType.NUMBER.value,
  ].includes(questionType);
  const useWarningIcon = isPromptQuestion || isChoiceQuestion;

  const suggestions = suggestionsData?.suggestions || [];

  useEffect(() => {
    if (!suggestions.length) return;

    if (
      ![OptionType.TEXT.value, OptionType.NUMBER.value].includes(questionType)
    ) {
      if (isChoiceQuestion) {
        const choiceSuggestions = suggestions as (
          | ChoiceSuggestion
          | ChoiceTextSuggestion
        )[];
        const ids = new Set(choiceSuggestions.map((s) => s.id));
        const choiceTextValues = new Set<string>();
        choiceSuggestions.forEach((s) => {
          if (s.type === OptionType.CHOICE_TEXT.value) {
            choiceTextValues.add((s as ChoiceTextSuggestion).value);
          }
        });
        setSelectedItems(ids);
        setSelectedChoiceTextValues(choiceTextValues);
      } else if (isPromptQuestion) {
        const promptSuggestions = suggestions as PromptSuggestion[];
        setSelectedItems(new Set(promptSuggestions.map((s) => s.block.id)));
      }
    }
  }, [suggestions, questionType, isChoiceQuestion, isPromptQuestion]);

  let descriptionKey: string;
  const titleKey = isPromptQuestion
    ? "survey.labels.previouslyAttachedEvidence"
    : "survey.labels.previousAnswer";
  if (isPromptQuestion) {
    descriptionKey = "survey.labels.ifAppliedEvidenceAdded";
  } else if (isChoiceQuestion) {
    descriptionKey = "survey.labels.ifAppliedOptionsAdded";
  } else if (isTextNumberQuestion) {
    descriptionKey = "survey.labels.ifAppliedAnswerReplaced";
  } else {
    descriptionKey = "survey.labels.previousAnswer";
  }

  const isApplyDisabled = useMemo(() => {
    if (isTextNumberQuestion) return false;

    return selectedItems.size === 0;
  }, [selectedItems, isTextNumberQuestion]);

  const handleItemChange = (
    itemId: number,
    checked: boolean,
    choiceTextValue?: string,
  ) => {
    if (choiceTextValue !== undefined) {
      setSelectedChoiceTextValues((prev) => {
        const next = new Set(prev);
        if (checked) {
          next.add(choiceTextValue);
        } else {
          next.delete(choiceTextValue);
        }
        return next;
      });

      setSelectedItems((prev) => {
        const next = new Set(prev);
        if (checked) {
          next.add(itemId);
        } else if (selectedChoiceTextValues.size <= 1) {
          next.delete(itemId);
        }
        return next;
      });
    } else {
      setSelectedItems((prev) => {
        const next = new Set(prev);
        if (checked) {
          next.add(itemId);
        } else {
          next.delete(itemId);
        }
        return next;
      });
    }
  };

  const handleApply = () => {
    if (!suggestions.length) return;

    if (isTextNumberQuestion) {
      const suggestion = suggestions[0] as TextNumberSuggestion;
      onApply({ type: SuggestionType.TEXT, value: suggestion.value });
    } else if (isChoiceQuestion) {
      onApply({
        type: SuggestionType.CHOICE,
        optionIds: selectedItems,
        choiceTextValues: selectedChoiceTextValues,
      });
    } else if (isPromptQuestion) {
      onApply({ type: SuggestionType.PROMPT, blockIds: selectedItems });
    }
  };

  if (isLoadingSuggestions) {
    return (
      <div className="flex items-center gap-200">
        <BaseSpinner size="extra-small" isFullWidth={false} />
        <p>{t("survey.labels.loading")}</p>
      </div>
    );
  }

  if (!suggestions.length) return null;

  return (
    <div className="question-suggestion-container flex flex-col items-start self-stretch">
      <div className="flex justify-between items-start self-stretch">
        <div className="flex flex-col">
          {suggestionsData && (
            <span className="body-m-medium">
              {t(titleKey, {
                surveyTitle: suggestionsData.sourceSurvey?.title,
              })}
            </span>
          )}

          <div className="flex gap-100">
            {useWarningIcon ? (
              <BaseSvgRenderer
                component={WarningCircle}
                width="20"
                height="20"
              />
            ) : (
              // TODO: Replace Warning with ValueIcon when user adds the new icon
              <BaseSvgRenderer
                component={WarningTriangle}
                width="20"
                height="20"
              />
            )}

            <span className="question-suggestion-description body-s-regular">
              {t(descriptionKey)}
            </span>
          </div>
        </div>

        <div className="flex gap-200">
          <BaseButton variant="link" onClick={onDismiss}>
            {t("survey.labels.dismiss")}
          </BaseButton>

          <BaseButton disabled={isApplyDisabled} onClick={handleApply}>
            {t("survey.labels.applyAnswer")}
          </BaseButton>
        </div>
      </div>

      <div className="question-suggestions-container flex flex-col gap-200 full-width">
        {isChoiceQuestion &&
          (suggestions as (ChoiceSuggestion | ChoiceTextSuggestion)[]).map(
            (suggestion, index) => {
              const isChoiceText =
                suggestion.type === OptionType.CHOICE_TEXT.value;

              const choiceTextValue = isChoiceText
                ? (suggestion as ChoiceTextSuggestion).value
                : undefined;

              const isDisabled = isChoiceText
                ? disabledSuggestions?.choiceTextValues.includes(
                    choiceTextValue!,
                  )
                : disabledSuggestions?.itemIds.includes(suggestion.id);

              const displayLabel = isChoiceText
                ? (suggestion as ChoiceTextSuggestion).value
                : (suggestion as ChoiceSuggestion).label;

              const isChecked =
                (isChoiceText
                  ? selectedChoiceTextValues.has(choiceTextValue!)
                  : selectedItems.has(suggestion.id)) || isDisabled;

              return (
                <CheckboxListItem
                  key={`${displayLabel}-${suggestion.id}-${index}`}
                  label={displayLabel}
                  showCheckbox={true}
                  checked={isChecked}
                  onChange={(checked) =>
                    handleItemChange(suggestion.id, checked, choiceTextValue)
                  }
                  isDisabled={isDisabled}
                />
              );
            },
          )}

        {isPromptQuestion &&
          (suggestions as PromptSuggestion[]).map((suggestion, index) => {
            const block = suggestion.block;
            const blockTitle = getEvidenceTitle(block) || "";
            const blockType = block.template.title;
            return (
              <CheckboxListItem
                key={`block-${block.id || index}`}
                label={blockTitle}
                showCheckbox={true}
                checked={
                  selectedItems.has(block.id) ||
                  disabledSuggestions?.itemIds?.includes(block.id)
                }
                onChange={(checked) => handleItemChange(block.id, checked)}
                tagLabel={blockType}
                description={t("survey.labels.lastModified", {
                  context:
                    [
                      block.updatedAt ? "date" : "",
                      block.createdBy?.fullName ? "user" : "",
                    ]
                      .filter(Boolean)
                      .join("_") || undefined,
                  date: DateTime.fromISO(block.updatedAt || "").toLocaleString(
                    DateTime.DATE_MED,
                  ),
                  user: block.createdBy?.fullName || "",
                })}
                isDisabled={disabledSuggestions?.itemIds?.includes(block.id)}
              />
            );
          })}

        {isTextNumberQuestion &&
          (suggestions as TextNumberSuggestion[]).map((suggestion) => (
            <CheckboxListItem
              key={`option-${suggestion.value}`}
              label={suggestion.value}
              showCheckbox={false}
            />
          ))}
      </div>
    </div>
  );
};

export default QuestionSuggestions;
